import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { BookingFlow } from "../booking-flows";
import { scheduleVisit } from "./api";
import { mutations, queries } from "./factories";
import {
  AdminScheduleVisitResponse,
  AppointmentSlotsParams,
  PatientVisit,
  ScheduleVisitResponse,
  StaffScheduling,
} from "./types";

const STALE_TIME = 1 * 60 * 1000; // consider data fresh for 1 minute, avoid calls when changing between views

export const useAppointmentSlotsByProviderQuery = ({
  params,
  enabled = true,
}: AppointmentSlotsParams) => {
  return useQuery({
    ...queries.appointmentSlotsByProvider(params),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME,
    enabled,
  });
};

export const useAppointmentSlotsByFirstAvailableQuery = ({
  params,
  enabled = true,
}: AppointmentSlotsParams) => {
  return useQuery({
    ...queries.appointmentSlotsByFirstAvailable(params),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME,
    enabled,
  });
};

type UseScheduleVisitOptions = {
  onSuccess?: (data: ScheduleVisitResponse) => void;
  onError?: (error: any) => void;
};

export const useScheduleVisitMutation = (options?: UseScheduleVisitOptions) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      bookingFlowId,
      visitData,
    }: {
      bookingFlowId: BookingFlow["id"];
      visitData: PatientVisit;
    }) => {
      return await scheduleVisit(bookingFlowId, visitData);
    },
    onSuccess: (data) => {
      queryClient.removeQueries({ queryKey: ["scheduling"] });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const useProvidersQuery = (
  stateId?: number | null,
  insuranceId?: number | null,
  appointmentType?: string,
) => {
  return useQuery({
    ...queries.providers(stateId, insuranceId, appointmentType),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useAppointmentTypesQuery = () => {
  return useQuery({
    ...queries.appointmentTypes(),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useRegisterAndBookMutation = (options?: {
  onMutate?: () => void;
  onSuccess?: (data: AdminScheduleVisitResponse) => void;
  onError?: (error: unknown) => void;
  onSettled?: () => void;
}) => {
  return useMutation({
    mutationFn: async ({ visitData }: { visitData: StaffScheduling }) => {
      return await mutations.registerAndBook({ visitData });
    },
    onMutate: () => {
      options?.onMutate?.();
    },
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
    onSettled: () => {
      options?.onSettled?.();
    },
  });
};
